import React, { useMemo } from 'react'
import { Field, Select } from '@workwhile/ui'
import { useAllTimeClocksForCompanyQuery } from 'queries/admin/timeClock/useAllTimeClocksForCompanyQuery'
import { useCompanyLocationsQuery } from 'queries/company/useCompanyLocationsQuery'

type ClockPickerProps = {
  companyId: number | null
  value: string | null
  onChange: (value: string | undefined) => void
}

export const ClockPicker = ({
  companyId,
  value,
  onChange,
}: ClockPickerProps) => {
  const { data: clocks, isLoading: isClocksLoading } =
    useAllTimeClocksForCompanyQuery(companyId, {
      enabled: companyId !== null,
    })
  const { data: locations } = useCompanyLocationsQuery(companyId ?? undefined)

  const locationNameById = useMemo(() => {
    const map = new Map<number, string>()
    locations?.forEach((location) => {
      map.set(Number(location.id), location.name)
    })
    return map
  }, [locations])

  const options = useMemo(() => {
    if (clocks === undefined) {
      return []
    }
    return clocks
      .slice()
      .sort((a, b) => a.vendorClockId.localeCompare(b.vendorClockId))
      .map((clock) => {
        const locationName =
          locationNameById.get(clock.locationId) ?? 'Unknown location'
        return {
          label: `${locationName} (${clock.locationId}) - Clock ${clock.vendorClockId}`,
          value: clock.vendorClockId,
        }
      })
  }, [clocks, locationNameById])

  const isDisabled = companyId === null

  return (
    <Field label="Clock">
      <Select
        block
        isClearable
        isDisabled={isDisabled}
        isLoading={!isDisabled && isClocksLoading}
        placeholder={
          isDisabled ? 'Select a company first' : 'All clocks for company'
        }
        options={options}
        value={options.find((option) => option.value === value) ?? null}
        onChange={(option) => {
          onChange(option?.value ?? undefined)
        }}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 10011,
            pointerEvents: 'auto',
          }),
        }}
      />
    </Field>
  )
}
